// src/pages/PayPage.jsx
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { initMercadoPago, Payment } from "@mercadopago/sdk-react";
import { paymentsCards, sendReserve } from "../api/pay";
import CardResumenReserva from "../components/CardResumenReserva";

const PayPage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    name,
    startDate,
    endDate,
    totalOffer,
    pricePerGuest,
    roomId,
    numGuests,
    unitIds,
    currency: stateCurrency, // 💱 viene desde HabitacionCard / HabitacionCardMobile
    propertyId, // 🏨 property dinámico
    mpPublicKey, // 🔑 MP del cliente
    mpLocale: stateMpLocale, // 🌎 locale opcional ("es-CL", "es-AR", etc.)
  } = state || {};

  // Si llegaron sin state (refresco directo / URL manual), mostramos algo amigable
  const hasBasicData = !!(
    name &&
    startDate &&
    endDate &&
    typeof totalOffer !== "undefined" &&
    roomId &&
    propertyId
  );

  // 💱 Moneda final de la reserva (ya venía convertida en getRealAvailability)
  const currency = (stateCurrency || "ARS").toUpperCase(); // "ARS" o "CLP"

  // Locale sugerido según moneda (podés ajustar esto si querés otra lógica)
  const mpLocale = stateMpLocale || (currency === "CLP" ? "es-CL" : "es-AR");

  // totalOffer ya viene en currency (ARS / CLP)
  const safeTotal = Number(totalOffer) || 0;
  const anticipo = Math.round(safeTotal * 0.5);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    confirmEmail: "",
    dni: "",
    numberphone: "",
    origin: "",
    age: "",
    reasonForVisit: "",
  });

  const isFormValid = () => {
    const requiredFields = [
      "fullName",
      "email",
      "confirmEmail",
      "dni",
      "numberphone",
    ];

    const allFieldsFilled = requiredFields.every(
      (field) => formData[field] && formData[field].toString().trim() !== ""
    );

    const emailsMatch = formData.email === formData.confirmEmail;

    return allFieldsFilled && emailsMatch;
  };

  const [reservationID, setReservationID] = useState(null);
  const [initialization, setInitialization] = useState(null);
  const [preferenceId, setPreferenceId] = useState(null);
  const [sandboxInitPoint, setSandboxInitPoint] = useState(null);
  const [prodInitPoint, setProdInitPoint] = useState(null);
  const [loading, setLoading] = useState(false);

  const [customization] = useState({
    paymentMethods: {
      creditCard: "all",
      debitCard: "all",
      bankTransfer: "all",
      atm: "all",
      onboarding_credits: "all",
      wallet_purchase: "all",
    },
  });

  const onError = (error) => {
    console.log("Error en el Brick: ", error);
  };

  const onReady = () => {
    console.log("El Brick está listo!");
  };

  // 🧠 MP genérico por cliente: la public key viene del user
  useEffect(() => {
    if (!mpPublicKey) {
      console.error("⚠️ Falta mpPublicKey en el state.");
      return;
    }

    initMercadoPago(mpPublicKey, {
      locale: mpLocale,
    });

    console.log("PayPage state:", {
      name,
      startDate,
      endDate,
      totalOffer: safeTotal,
      currency,
      propertyId,
      mpLocale,
      mpPublicKey,
    });
  }, [
    mpPublicKey,
    mpLocale,
    name,
    startDate,
    endDate,
    safeTotal,
    currency,
    propertyId,
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (loading) return;

    if (!isFormValid()) {
      alert("Verifica los datos del formulario.");
      return;
    }

    if (!propertyId) {
      alert("No se encontró la propiedad. Reintentá la reserva.");
      return;
    }

    const body = {
      cliente: {
        ...formData,
      },
      reserva: {
        roomName: name,
        roomId,
        unitId: unitIds,
        pricePerGuest, // 💱 ya viene en currency (ARS/CLP)
        startDate,
        endDate,
        total: safeTotal, // 💱 total en currency
        anticipo, // 💱 mitad del total, misma currency
        numGuests,
        propertyId,
        currency, // "ARS" o "CLP"
      },
    };

    try {
      setLoading(true);

      const pref = await sendReserve(body);
      console.log("✅ Reserva enviada:", pref.data);

      setReservationID(pref.data.reservationId);
      setPreferenceId(pref.data.id);
      setSandboxInitPoint(pref.data.sandbox_init_point);
      setProdInitPoint(pref.data.init_point);

      // MP Brick: amount = anticipo en la moneda que maneja la cuenta MP de ese cliente
      setInitialization({
        amount: anticipo,
        preferenceId: pref.data.id,
      });
    } catch (err) {
      console.error("❌ Error creando reserva:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlerPayMP = (url) => {
    window.open(url, "_blank");
  };

  // Handler usado por el Brick
  const onSubmit = async ({ formData: mpData }) => {
    return new Promise((resolve, reject) => {
      if (!reservationID) return reject("Reserva no iniciada");

      paymentsCards({ formData: mpData, reservationId: reservationID })
        .then(() => {
          navigate("/success");
          resolve();
        })
        .catch((err) => {
          console.error("Error de pago:", err);
          reject();
        });
    });
  };

  // 🔒 Fallback visual si falta info crítica
  if (!state || !hasBasicData) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
          p: 3,
        }}
      >
        <Typography variant="h6" align="center">
          No se pudo recuperar los datos de la reserva.
        </Typography>
        <Button variant="contained" onClick={() => navigate("/")}>
          Volver al inicio
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        backgroundColor: "#f3efe5",
        minHeight: "100vh",
        padding: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 4,
          width: "100%",
          maxWidth: 1200,
        }}
      >
        {/* Resumen de reserva */}
        <Box
          sx={{
            width: isMobile ? "100%" : 360,
            position: isMobile ? "static" : "sticky",
            top: 100,
            alignSelf: "flex-start",
          }}
        >
          <CardResumenReserva
            roomName={name}
            startDate={startDate}
            endDate={endDate}
            pricePerNight={pricePerGuest}
            total={safeTotal}
            numGuests={numGuests}
            anticipo={anticipo}
            currency={currency} // 💱 ARS / CLP para mostrar en UI
          />
        </Box>

        {/* Contenido principal */}
        <Box
          sx={{
            backgroundColor: "#fff",
            padding: 4,
            borderRadius: 3,
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.1)",
            flex: 1,
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{ fontWeight: "bold", textAlign: "center", mb: 3 }}
          >
            Confirmar y Pagar Reserva
          </Typography>

          {(!preferenceId || !initialization) && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {["fullName", "email", "confirmEmail", "dni", "numberphone"].map(
                (field) => (
                  <TextField
                    key={field}
                    fullWidth
                    required
                    name={field}
                    label={
                      {
                        fullName: "Nombre completo",
                        email: "Correo electrónico",
                        confirmEmail: "Confirmar correo",
                        dni: "DNI / Pasaporte",
                        numberphone: "Número de teléfono",
                      }[field] || field
                    }
                    type={field === "age" ? "number" : "text"}
                    value={formData[field]}
                    onChange={handleInputChange}
                    variant="outlined"
                    sx={{ backgroundColor: "#f8f9fa" }}
                  />
                )
              )}

              <Button
                variant="contained"
                fullWidth
                onClick={handleSubmit}
                disabled={!isFormValid() || loading}
                sx={{
                  mt: 3,
                  py: 1.5,
                  fontWeight: "bold",
                  fontSize: "1rem",
                  backgroundColor: "#0071e3",
                  "&:hover": {
                    backgroundColor: "#005bb5",
                  },
                }}
              >
                {loading ? "Procesando..." : "Continuar a Pago"}
              </Button>
            </Box>
          )}

          {preferenceId && initialization && (
            <>
              <Divider sx={{ my: 4 }}>Métodos de pago</Divider>

              <Payment
                initialization={initialization}
                customization={customization}
                onSubmit={onSubmit}
                onReady={onReady}
                onError={onError}
              />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PayPage;
